import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Play, X, ChevronLeft, ChevronRight, Check,
  LayoutDashboard, CalendarDays, Building2, Link as LinkIcon,
  Wallet, CreditCard, Activity, UserSquare2, Sparkles, Zap,
} from 'lucide-react';

const FONT_DISPLAY = "'Space Grotesk', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";

const SLIDES = [
  { image: '/screenshots/dashboard.jpg', title: 'Smart Dashboard', description: "Today's appointments, task queue, and quote pipeline — at a glance, every morning." },
  { image: '/screenshots/calendar.jpg', title: 'Integrated Calendar', description: 'Filter by team member, sync with Google Calendar, switch between month / week / day in a tap.' },
  { image: '/screenshots/listings.jpg', title: 'Property Listings', description: 'Every shoot in one searchable grid. Tag it, track delivery, ship a marketing site per listing.' },
  { image: '/screenshots/orderform.jpg', title: 'White-Label Booking', description: 'Your brand, your URL. Clients book themselves — you wake up to a full calendar.' },
];

const PRIMARY_FEATURES = [
  {
    id: 'dashboard',
    icon: LayoutDashboard,
    title: 'A dashboard that actually tells you what to do today.',
    body: 'Open the app and see today\'s appointments, the tasks that need closing, the quotes waiting on a yes. No more hunting through tabs to figure out where the day stands.',
    bullets: ['Today\'s schedule front and center', 'Overdue / due-today task triage', 'Live quote pipeline (Pending → Accepted → Cancelled)'],
    image: '/screenshots/dashboard-features.jpg',
  },
  {
    id: 'calendar',
    icon: CalendarDays,
    title: 'A calendar built for teams, not individuals.',
    body: 'Filter chips for every photographer. Drag-to-reschedule. Block off your weekends with one click. Two-way Google Calendar sync so nothing ever double-books.',
    bullets: ['Team-member filter chips', 'Google Calendar two-way sync', 'Month / Week / Day / Agenda views'],
    image: '/screenshots/calendar.jpg',
  },
  {
    id: 'listings',
    icon: Building2,
    title: 'Every property organized. Every file delivered.',
    body: 'Drag-to-reorder photos. Tag any listing as Rush or Twilight. Spin up a one-page marketing website for every address — branded, mobile-ready, instant.',
    bullets: ['Photos, videos, floor plans, 3D tours', 'Per-listing marketing websites', 'Tags, status, search, bulk actions'],
    image: '/screenshots/listings.jpg',
  },
  {
    id: 'orderform',
    icon: LinkIcon,
    title: 'A booking link that sells while you sleep.',
    body: 'Share one URL. Clients pick services, pick a time, pay a deposit (or in full) on the spot. White-label it with your logo, your brand color, your custom domain.',
    bullets: ['Custom booking URL (your-brand.com/b/...)', 'Brand color + logo white-label', 'Deposit or full payment at checkout'],
    image: '/screenshots/orderform.jpg',
  },
];

const BENTO_FEATURES = [
  { icon: Wallet, title: 'Payroll, automated', body: 'Per-tier payouts, PDF pay stubs, bulk email to your team in one click.', testid: 'feature-payroll' },
  { icon: CreditCard, title: 'Stripe payments', body: 'Saved cards, charge-on-file, refunds, and automated payment reminders.', testid: 'feature-stripe' },
  { icon: Activity, title: 'Full activity log', body: 'Every change, every payment, every download — audit-grade timeline.', testid: 'feature-activity' },
  { icon: UserSquare2, title: 'Client portal', body: 'Clients pay balances, download media, manage saved cards themselves.', testid: 'feature-portal' },
];

const STEPS = [
  { n: '01', title: 'Sign up', body: 'Create your account in 60 seconds. No card required to start.' },
  { n: '02', title: 'Add your services', body: 'Define your packages, pricing, and team. Set your booking rules.' },
  { n: '03', title: 'Share your link', body: 'Send clients your white-labeled booking URL. The work starts itself.' },
];

const LIVE_FEED = [
  { name: 'Liam Donovan', action: 'just booked a HDR + Drone package for 728 Crockett Springs Ct', when: 'just now' },
  { name: 'Aaliyah Thompson', action: 'paid $1,440 for 4218 Belmont Park Terrace', when: '2m ago' },
  { name: 'Sophia Martinez', action: 'downloaded the delivery for 5604 Westhaven Drive', when: '5m ago' },
  { name: 'Marcus Chen', action: 'accepted a quote for $785 in Brentwood', when: '8m ago' },
  { name: 'Harper Sullivan', action: 'rescheduled to Friday at 10:30 AM', when: '12m ago' },
  { name: 'Chloe Nguyen', action: 'paid a 25% deposit on 3120 West End Avenue', when: '17m ago' },
];

const FAQS = [
  { q: 'Is there a free trial?', a: 'Yes — you can sign up, set everything up, and start booking with no commitment. Pay only when a listing closes.' },
  { q: 'How does $5 per listing work?', a: 'You pay $5 per listing that gets booked and completed through est8.media. No subscriptions, no per-seat fees, no hidden charges.' },
  { q: 'Can clients pay through the platform?', a: 'Yes. Bring your own Stripe account, choose deposit or full payment at booking, and let clients pay invoices or remaining balances from the client portal.' },
  { q: 'Do you support multiple photographers?', a: 'Absolutely. Add unlimited team members, assign jobs, calculate payroll per-person with per-tier overrides, and email pay stubs in bulk.' },
];

const LandingPage = () => {
  const [demoOpen, setDemoOpen] = useState(false);
  const [slide, setSlide] = useState(0);
  const [openFaq, setOpenFaq] = useState(-1);
  const [statIdx, setStatIdx] = useState(0);
  const [tickerIdx, setTickerIdx] = useState(0);

  const HERO_STATS = [
    { label: 'Avg. payout', value: '$1,840 / week', icon: Zap, color: '#FFB000' },
    { label: 'Listings this month', value: '127 closed', icon: Building2, color: '#00E5FF' },
    { label: 'Today\'s shoots', value: '8 scheduled', icon: CalendarDays, color: '#FFB000' },
    { label: 'Avg. response', value: '12 min', icon: Activity, color: '#00E5FF' },
  ];

  useEffect(() => {
    const id = window.setInterval(() => setStatIdx(i => (i + 1) % 4), 3500);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => setTickerIdx(i => (i + 1) % LIVE_FEED.length), 4500);
    return () => window.clearInterval(id);
  }, []);

  const next = useCallback(() => setSlide(s => (s + 1) % SLIDES.length), []);
  const prev = useCallback(() => setSlide(s => (s - 1 + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    if (!demoOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setDemoOpen(false);
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [demoOpen, next, prev]);

  const scrollTo = (e, id) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div
      className="min-h-screen bg-[#0a0a0f] text-white overflow-x-hidden"
      style={{ fontFamily: FONT_BODY }}
      data-testid="landing-page"
    >
      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      {/* Background grain + radial accent */}
      <div className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[radial-gradient(circle,rgba(0,229,255,0.12),transparent_60%)] blur-3xl" />
        <div className="absolute top-1/2 -right-32 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(255,176,0,0.08),transparent_60%)] blur-3xl" />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3CfeColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.6 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
      </div>

      {/* DEMO MODAL */}
      {demoOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="demo-modal">
          <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={() => setDemoOpen(false)} />
          <div className="relative w-full max-w-5xl">
            <div className="flex items-center justify-between mb-3 text-xs uppercase tracking-[0.2em] text-white/60">
              <span>{String(slide + 1).padStart(2, '0')} / {String(SLIDES.length).padStart(2, '0')}</span>
              <button onClick={() => setDemoOpen(false)} className="hover:text-white transition-colors p-1.5 rounded-full bg-white/5 border border-white/10" data-testid="demo-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="bg-[#12121a] rounded-2xl overflow-hidden border border-white/10 shadow-[0_0_60px_rgba(0,229,255,0.1)]">
              <div className="flex items-center gap-2 px-4 py-3 bg-[#0d0d14] border-b border-white/5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                <div className="ml-3 text-xs text-white/40 font-mono">est8.media/{SLIDES[slide].title.toLowerCase().replace(/\s+/g, '-')}</div>
              </div>
              <img src={SLIDES[slide].image} alt={SLIDES[slide].title} className="w-full aspect-video object-cover object-top" />
              <div className="p-8 border-t border-white/5">
                <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: FONT_DISPLAY }}>{SLIDES[slide].title}</h3>
                <p className="text-white/60 leading-relaxed">{SLIDES[slide].description}</p>
              </div>
            </div>
            <div className="flex items-center justify-between mt-4">
              <button onClick={prev} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors" data-testid="demo-prev">
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <div className="flex gap-1.5">
                {SLIDES.map((_, i) => (
                  <button key={i} onClick={() => setSlide(i)} className={`h-1.5 rounded-full transition-all ${i === slide ? 'w-8 bg-[#00E5FF]' : 'w-1.5 bg-white/20 hover:bg-white/40'}`} data-testid={`demo-dot-${i}`} />
                ))}
              </div>
              <button onClick={next} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors" data-testid="demo-next">
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-2xl bg-[#0a0a0f]/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#00E5FF] to-[#00B8D4] flex items-center justify-center font-bold text-black text-sm" style={{ fontFamily: FONT_DISPLAY }}>E8</div>
            <span className="text-lg font-bold" style={{ color: 'inherit', fontFamily: FONT_DISPLAY, letterSpacing: '-0.02em' }}>est8.media</span>
          </div>
          <div className="hidden md:flex items-center gap-1">
            <a href="#features" onClick={(e) => scrollTo(e, 'features')} className="px-4 py-2 text-sm text-white/70 hover:text-white transition-colors" data-testid="nav-features">Features</a>
            <a href="#how" onClick={(e) => scrollTo(e, 'how')} className="px-4 py-2 text-sm text-white/70 hover:text-white transition-colors" data-testid="nav-how">How it works</a>
            <a href="#pricing" onClick={(e) => scrollTo(e, 'pricing')} className="px-4 py-2 text-sm text-white/70 hover:text-white transition-colors" data-testid="nav-pricing">Pricing</a>
            <a href="#faq" onClick={(e) => scrollTo(e, 'faq')} className="px-4 py-2 text-sm text-white/70 hover:text-white transition-colors" data-testid="nav-faq">FAQ</a>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login" className="hidden sm:inline-block px-4 py-2 text-sm text-white/70 hover:text-white transition-colors" data-testid="nav-login">Login</Link>
            <Link to="/login" className="px-4 py-2 text-sm font-medium rounded-full bg-[#00E5FF] text-black hover:bg-white transition-colors" data-testid="nav-cta">Get started</Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative z-10 pt-36 md:pt-44 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-7 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-[0.2em] text-white/70" data-testid="hero-pill">
            <Sparkles className="w-3.5 h-3.5 text-[#00E5FF]" /> Built by photographers, for photographers
          </div>
          <h1 className="font-bold tracking-tight mb-7 max-w-5xl" style={{ color: 'inherit', fontFamily: FONT_DISPLAY, fontSize: 'clamp(2.75rem, 7vw, 5.5rem)', lineHeight: '0.95', letterSpacing: '-0.04em' }} data-testid="hero-headline">
            Schedule it.<br />
            Shoot it.<br />
            <span className="text-[#00E5FF]">Get paid.</span> <span className="text-white/30">No chaos.</span>
          </h1>
          <p className="text-lg md:text-xl text-white/60 max-w-2xl mb-10 leading-relaxed">
            The end-to-end operating system for real estate photography businesses. Bookings, payments, delivery, payroll — all in one place.
          </p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Link to="/login" className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#00E5FF] text-black font-semibold hover:bg-white transition-colors" data-testid="hero-cta-primary">
              Start free <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <button onClick={() => { setSlide(0); setDemoOpen(true); }} className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors font-medium" data-testid="hero-cta-watch">
              <Play className="w-4 h-4 fill-[#00E5FF] text-[#00E5FF]" /> Watch the 60-sec tour
            </button>
          </div>

          {/* Live ticker — auto-rotating social proof feed */}
          <div className="mt-12 max-w-2xl" data-testid="live-ticker">
            <div className="flex items-center gap-3 px-4 py-3 rounded-full bg-white/[0.03] border border-white/10 backdrop-blur-sm">
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex-shrink-0">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300">Live</span>
              </div>
              <div key={tickerIdx} className="flex-1 min-w-0 text-sm text-white/80 truncate" style={{ animation: 'fadeIn 500ms ease-out' }}>
                <span className="font-semibold">{LIVE_FEED[tickerIdx].name}</span>{' '}
                <span className="text-white/55">{LIVE_FEED[tickerIdx].action}</span>
              </div>
              <span className="text-xs text-white/30 flex-shrink-0">{LIVE_FEED[tickerIdx].when}</span>
            </div>
          </div>

          {/* Hero screenshot — tilted glass frame */}
          <div className="relative mt-20 md:mt-24" data-testid="hero-screenshot">
            <div className="absolute -inset-x-12 -top-20 -bottom-20 bg-[radial-gradient(circle_at_50%_50%,rgba(0,229,255,0.18),transparent_55%)] blur-3xl pointer-events-none" />
            <div className="relative rounded-2xl overflow-hidden border border-white/10 ring-1 ring-white/5 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)]">
              <div className="flex items-center gap-2 px-4 py-3 bg-[#0d0d14] border-b border-white/5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                <div className="ml-3 text-xs text-white/40 font-mono">est8.media/app/dashboard</div>
              </div>
              <img src="/screenshots/dashboard.jpg" alt="est8.media dashboard preview" className="w-full block" />
            </div>
            {/* Floating chips */}
            <div className="hidden md:flex absolute -top-6 -left-6 items-center gap-2 px-4 py-2.5 rounded-full bg-[#12121a]/95 border border-white/10 backdrop-blur-xl shadow-xl">
              <div className="w-2 h-2 rounded-full bg-[#00E5FF] animate-pulse" />
              <span className="text-xs font-medium">3 new bookings today</span>
            </div>
            <div className="hidden md:flex absolute -bottom-5 right-6 items-center gap-2.5 px-4 py-3 rounded-2xl bg-[#12121a]/95 border border-white/10 backdrop-blur-xl shadow-xl min-w-[220px]" data-testid="hero-stat-chip">
              {(() => {
                const s = HERO_STATS[statIdx];
                const Icon = s.icon;
                return (
                  <div key={statIdx} className="flex items-center gap-2.5 w-full" style={{ animation: 'fadeIn 500ms ease-out' }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${s.color}26` }}>
                      <Icon className="w-4 h-4" style={{ color: s.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-white/50 uppercase tracking-wider truncate">{s.label}</div>
                      <div className="text-sm font-semibold truncate">{s.value}</div>
                    </div>
                    <div className="flex gap-1">
                      {HERO_STATS.map((_, i) => (
                        <span key={i} className={`block w-1 h-1 rounded-full transition-colors ${i === statIdx ? 'bg-[#00E5FF]' : 'bg-white/15'}`} />
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="relative z-10 px-6 py-8 border-y border-white/5">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-white/40 text-xs uppercase tracking-[0.25em]">
          <span>Stripe Payments</span>
          <span className="w-1 h-1 rounded-full bg-white/20" />
          <span>Google Calendar</span>
          <span className="w-1 h-1 rounded-full bg-white/20" />
          <span>Dropbox Sync</span>
          <span className="w-1 h-1 rounded-full bg-white/20" />
          <span>AWS S3 Storage</span>
          <span className="w-1 h-1 rounded-full bg-white/20" />
          <span>Resend Email</span>
        </div>
      </section>

      {/* FEATURES — alternating Z-rows */}
      <section id="features" className="relative z-10 px-6 py-24 md:py-32" data-testid="section-features">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-20">
            <div className="text-xs uppercase tracking-[0.25em] text-[#00E5FF] mb-4">Features</div>
            <h2 className="font-bold tracking-tight mb-5" style={{ color: 'inherit', fontFamily: FONT_DISPLAY, fontSize: 'clamp(2rem, 4.5vw, 3.75rem)', lineHeight: '1.0', letterSpacing: '-0.03em' }}>
              Every tool you need.<br />
              <span className="text-white/40">None you don't.</span>
            </h2>
            <p className="text-lg text-white/60 leading-relaxed">A purpose-built suite — that can be tailored to fit your workflow.</p>
          </div>

          <div className="space-y-24 md:space-y-32">
            {PRIMARY_FEATURES.map((f, idx) => {
              const Icon = f.icon;
              const reversed = idx % 2 === 1;
              return (
                <div key={f.id} className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center" data-testid={`feature-${f.id}`}>
                  <div className={`lg:col-span-5 ${reversed ? 'lg:order-2' : ''}`}>
                    <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-white/5 border border-white/10 mb-5">
                      <Icon className="w-5 h-5 text-[#00E5FF]" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-bold tracking-tight mb-4" style={{ color: 'inherit', fontFamily: FONT_DISPLAY, fontSize: 'clamp(1.65rem, 2.5vw, 2.25rem)', lineHeight: '1.1', letterSpacing: '-0.02em' }}>
                      {f.title}
                    </h3>
                    <p className="text-white/60 leading-relaxed mb-6">{f.body}</p>
                    <ul className="space-y-2.5">
                      {f.bullets.map(b => (
                        <li key={b} className="flex items-start gap-3 text-sm text-white/80">
                          <Check className="w-4 h-4 mt-0.5 text-[#00E5FF] flex-shrink-0" strokeWidth={2.5} />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className={`lg:col-span-7 ${reversed ? 'lg:order-1' : ''}`}>
                    <div className="relative group">
                      <div className="absolute -inset-3 bg-[radial-gradient(circle_at_50%_50%,rgba(0,229,255,0.15),transparent_70%)] opacity-0 group-hover:opacity-100 blur-2xl transition-opacity duration-700 pointer-events-none" />
                      <div className="relative rounded-xl overflow-hidden border border-white/10 ring-1 ring-white/5 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.5)]">
                        <div className="flex items-center gap-1.5 px-3 py-2 bg-[#0d0d14] border-b border-white/5">
                          <div className="w-2 h-2 rounded-full bg-red-500/50" />
                          <div className="w-2 h-2 rounded-full bg-yellow-500/50" />
                          <div className="w-2 h-2 rounded-full bg-green-500/50" />
                        </div>
                        <img src={f.image} alt={f.title} className="w-full block" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bento grid of secondary features */}
          <div className="mt-32 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {BENTO_FEATURES.map(b => {
              const Icon = b.icon;
              return (
                <div
                  key={b.title}
                  className="group p-7 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-white/20 hover:bg-white/[0.05] hover:-translate-y-1 transition-all duration-300"
                  data-testid={b.testid}
                >
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#00E5FF]/15 to-[#FFB000]/15 border border-white/10 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-[#00E5FF] group-hover:text-[#FFB000] transition-colors" strokeWidth={1.5} />
                  </div>
                  <h4 className="font-semibold text-lg mb-2" style={{ color: 'inherit', fontFamily: FONT_DISPLAY, letterSpacing: '-0.01em' }}>{b.title}</h4>
                  <p className="text-sm text-white/55 leading-relaxed">{b.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="relative z-10 px-6 py-24 md:py-32 border-y border-white/5 bg-[#0c0c14]" data-testid="section-how">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-16">
            <div className="text-xs uppercase tracking-[0.25em] text-[#FFB000] mb-4">How it works</div>
            <h2 className="font-bold tracking-tight" style={{ color: 'inherit', fontFamily: FONT_DISPLAY, fontSize: 'clamp(2rem, 4.5vw, 3.75rem)', lineHeight: '1.0', letterSpacing: '-0.03em' }}>
              From zero to your first booking in 3 steps.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.n} className="relative p-8 rounded-2xl bg-white/[0.03] border border-white/10" data-testid={`step-${s.n}`}>
                <div className="text-[5rem] leading-none font-bold text-white/[0.06] absolute top-4 right-5 select-none" style={{ fontFamily: FONT_DISPLAY }}>{s.n}</div>
                <div className="relative">
                  <div className="text-xs uppercase tracking-[0.25em] text-[#00E5FF] mb-3">Step {s.n}</div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: 'inherit', fontFamily: FONT_DISPLAY, letterSpacing: '-0.01em' }}>{s.title}</h3>
                  <p className="text-white/55 leading-relaxed text-sm">{s.body}</p>
                </div>
                {i < STEPS.length - 1 && <ArrowRight className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 w-6 h-6 text-white/20" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="relative z-10 px-6 py-24 md:py-32" data-testid="section-pricing">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-xs uppercase tracking-[0.25em] text-[#FFB000] mb-4">Pricing</div>
          <h2 className="font-bold tracking-tight mb-5" style={{ color: 'inherit', fontFamily: FONT_DISPLAY, fontSize: 'clamp(2rem, 4.5vw, 3.75rem)', lineHeight: '1.0', letterSpacing: '-0.03em' }}>
            One price. <span className="text-[#FFB000]">No surprises.</span>
          </h2>
          <p className="text-lg text-white/60 mb-12 leading-relaxed">No bulk monthly subscription costs. Only pay for what you need.</p>

          <div className="relative inline-block w-full max-w-md" data-testid="pricing-card">
            <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-[#00E5FF] to-[#FFB000] opacity-30 blur-xl" />
            <div className="relative p-10 rounded-3xl bg-[#12121a] border border-white/10">
              <div className="flex items-baseline justify-center gap-2 mb-2">
                <span className="text-xl text-white/40 font-medium">Just</span>
                <span className="font-bold text-7xl" style={{ color: 'inherit', fontFamily: FONT_DISPLAY, letterSpacing: '-0.04em' }}>$5</span>
                <span className="text-xl text-white/60 font-medium">/ listing</span>
              </div>
              <div className="text-sm text-white/50 mb-8">
                <span className="text-[#FFB000] font-semibold">Simple pricing — always.</span>
              </div>
              <ul className="space-y-3 text-left mb-8">
                {[
                  'Unlimited team members',
                  'Unlimited clients & properties',
                  'White-label booking link',
                  'Stripe payments + saved cards',
                  'Payroll, pay stubs, and bulk email',
                  'Activity log + client portal',
                  'Google Calendar + Dropbox sync',
                  'Marketing websites per listing',
                ].map(item => (
                  <li key={item} className="flex items-start gap-3 text-sm text-white/85">
                    <Check className="w-4 h-4 mt-0.5 text-[#00E5FF] flex-shrink-0" strokeWidth={2.5} />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/login" className="block w-full text-center py-3.5 rounded-full bg-[#00E5FF] text-black font-semibold hover:bg-white transition-colors" data-testid="pricing-cta">
                Start free — no card required
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative z-10 px-6 py-24 md:py-32 border-t border-white/5" data-testid="section-faq">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-14">
            <div className="text-xs uppercase tracking-[0.25em] text-[#00E5FF] mb-4">FAQ</div>
            <h2 className="font-bold tracking-tight" style={{ color: 'inherit', fontFamily: FONT_DISPLAY, fontSize: 'clamp(2rem, 4.5vw, 3.75rem)', lineHeight: '1.0', letterSpacing: '-0.03em' }}>
              Questions, answered.
            </h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <button
                  key={i}
                  onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  className="w-full text-left p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-white/20 transition-colors"
                  data-testid={`faq-${i}`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-medium text-white/90" style={{ fontFamily: FONT_DISPLAY }}>{f.q}</span>
                    <span className={`w-7 h-7 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 transition-transform ${isOpen ? 'rotate-45' : ''}`}>
                      <span className="text-white/60">+</span>
                    </span>
                  </div>
                  {isOpen && <p className="mt-4 text-sm text-white/60 leading-relaxed">{f.a}</p>}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative z-10 px-6 py-24 md:py-32">
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-3xl p-12 md:p-16 overflow-hidden border border-white/10 bg-gradient-to-br from-[#0f1722] via-[#0a0a0f] to-[#1a0f10]">
            <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[radial-gradient(circle,rgba(0,229,255,0.25),transparent_70%)] blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-[radial-gradient(circle,rgba(255,176,0,0.18),transparent_70%)] blur-3xl" />
            <div className="relative">
              <h2 className="font-bold tracking-tight mb-5 max-w-2xl" style={{ color: 'inherit', fontFamily: FONT_DISPLAY, fontSize: 'clamp(2rem, 4.5vw, 3.75rem)', lineHeight: '1.0', letterSpacing: '-0.03em' }}>
                Ready to stop juggling tools?
              </h2>
              <p className="text-lg text-white/60 mb-9 max-w-xl">Spin up your account, share your booking link, and watch the chaos go away.</p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Link to="/login" className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#00E5FF] text-black font-semibold hover:bg-white transition-colors" data-testid="final-cta-primary">
                  Start your free account <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a href="#pricing" onClick={(e) => scrollTo(e, 'pricing')} className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors font-medium" data-testid="final-cta-pricing">
                  See pricing
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative z-10 px-6 py-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#00E5FF] to-[#00B8D4] flex items-center justify-center font-bold text-black text-xs" style={{ fontFamily: FONT_DISPLAY }}>E8</div>
            <span className="font-semibold" style={{ color: 'inherit', fontFamily: FONT_DISPLAY, letterSpacing: '-0.02em' }}>est8.media</span>
            <span className="ml-2 text-xs text-white/30">© {new Date().getFullYear()}</span>
          </div>
          <div className="flex items-center gap-5 text-sm text-white/50">
            <a href="#features" onClick={(e) => scrollTo(e, 'features')} className="hover:text-white transition-colors">Features</a>
            <a href="#pricing" onClick={(e) => scrollTo(e, 'pricing')} className="hover:text-white transition-colors">Pricing</a>
            <a href="#faq" onClick={(e) => scrollTo(e, 'faq')} className="hover:text-white transition-colors">FAQ</a>
            <Link to="/login" className="hover:text-white transition-colors">Login</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
